import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Ticket,
  Search,
  User,
  UserPlus,
  Mail,
  Phone,
  DollarSign,
  CreditCard,
  Banknote,
  FileText,
  CheckCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Printer,
  Send,
  Download,
  Eye,
  ExternalLink
} from 'lucide-react';
import { API_URL as API } from '../../config/api';

const ManualTicketManager = () => {
  const [mode, setMode] = useState('new'); // 'new' or 'history'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isNewPerson, setIsNewPerson] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    guest_city: '',
    guest_country: 'Haïti',
    quantity: 1,
    amount_paid: 50,
    payment_method: 'cash',
    notes: '',
    is_test: false
  });
  
  // History
  const [manualTickets, setManualTickets] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPages, setHistoryPages] = useState(1);
  const [showTestTickets, setShowTestTickets] = useState(true); // Filter for test tickets
  
  const [submitting, setSubmitting] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);

  const token = localStorage.getItem('session_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Price tiers (1-3-6-9-12 + Autre)
  const priceTiers = [
    { quantity: 1, price: 50, label: '1 billet', description: '$50' },
    { quantity: 3, price: 100, label: '3 billets', description: '$100 (économisez $50!)' },
    { quantity: 6, price: 200, label: '6 billets', description: '$200 (économisez $100!)' },
    { quantity: 9, price: 300, label: '9 billets', description: '$300 (économisez $150!)' },
    { quantity: 12, price: 400, label: '12 billets', description: '$400 (économisez $200!)' },
    { quantity: 'custom', price: null, label: 'Autre', description: 'Quantité personnalisée' }
  ];

  const [selectedTier, setSelectedTier] = useState(null);
  const [customQuantity, setCustomQuantity] = useState(1);

  // Calculate price based on quantity (3 for $100 rule)
  const calculatePrice = (qty) => {
    if (qty <= 0) return 0;
    const bundlesOf3 = Math.floor(qty / 3);
    const remainder = qty % 3;
    return (bundlesOf3 * 100) + (remainder * 50);
  };

  // Handle tier selection
  const handleTierSelect = (tier) => {
    setSelectedTier(tier);
    if (tier.quantity !== 'custom') {
      setFormData({
        ...formData,
        quantity: tier.quantity,
        amount_paid: tier.price
      });
    }
  };

  // Handle custom quantity change
  const handleCustomQuantityChange = (qty) => {
    const quantity = parseInt(qty) || 1;
    setCustomQuantity(quantity);
    setFormData({
      ...formData,
      quantity: quantity,
      amount_paid: calculatePrice(quantity)
    });
  };

  const paymentMethods = [
    { id: 'cash', label: 'Cash / Espèces', icon: Banknote, color: 'text-green-600' },
    { id: 'transfer', label: 'Virement bancaire', icon: CreditCard, color: 'text-blue-600' },
    { id: 'check', label: 'Chèque', icon: FileText, color: 'text-purple-600' },
    { id: 'moncash', label: 'Moncash', icon: Phone, color: 'text-orange-600' },
    { id: 'natcash', label: 'Natcash', icon: Phone, color: 'text-blue-500' },
    { id: 'paypal', label: 'PayPal', icon: DollarSign, color: 'text-blue-700' },
    { id: 'interac', label: 'Virement Interac', icon: CreditCard, color: 'text-yellow-600' },
    { id: 'other', label: 'Autre', icon: DollarSign, color: 'text-gray-600' }
  ];

  useEffect(() => {
    if (mode === 'history') {
      loadManualTickets();
    }
  }, [mode, historyPage, showTestTickets]);

  const searchUsers = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/admin/users/search`, {
        params: { q: query },
        headers,
        withCredentials: true
      });
      setSearchResults(response.data.users || []);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const loadManualTickets = async () => {
    try {
      const params = { page: historyPage, limit: 15 };
      if (!showTestTickets) {
        params.exclude_test = true;
      }
      const response = await axios.get(`${API}/admin/tickets/manual`, {
        params,
        headers,
        withCredentials: true
      });
      setManualTickets(response.data.tickets || []);
      setHistoryTotal(response.data.total);
      setHistoryPages(response.data.pages);
    } catch (error) {
      console.error('Error loading manual tickets:', error);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setIsNewPerson(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleNewPerson = () => {
    setSelectedUser(null);
    setIsNewPerson(true);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!selectedUser && !isNewPerson) {
      toast.error('Veuillez sélectionner un utilisateur ou créer un nouveau bénéficiaire');
      return;
    }
    
    if (isNewPerson && !formData.guest_name) {
      toast.error('Le nom du bénéficiaire est requis');
      return;
    }
    
    // IMPORTANT: Email requis pour que les billets apparaissent dans le tableau de bord
    if (isNewPerson && !formData.guest_email) {
      toast.error('L\'email est requis pour que les billets apparaissent dans le compte de l\'utilisateur');
      return;
    }
    
    if (formData.quantity < 1) {
      toast.error('La quantité doit être au moins 1');
      return;
    }

    setSubmitting(true);

    try {
      const requestData = {
        quantity: formData.quantity,
        amount_paid: formData.amount_paid,
        payment_method: formData.payment_method,
        notes: formData.notes || undefined,
        is_test: formData.is_test
      };

      if (selectedUser) {
        requestData.user_id = selectedUser.user_id;
      } else {
        requestData.guest_name = formData.guest_name;
        requestData.guest_email = formData.guest_email || undefined;
        requestData.guest_phone = formData.guest_phone || undefined;
        requestData.guest_city = formData.guest_city || undefined;
        requestData.guest_country = formData.guest_country || undefined;
      }

      const response = await axios.post(`${API}/admin/tickets/manual`, requestData, {
        headers,
        withCredentials: true
      });

      setLastCreated(response.data);
      toast.success(`${response.data.tickets.length} billet(s) créé(s) avec succès !`);
      
      // Reset form
      setSelectedUser(null);
      setIsNewPerson(false);
      setSelectedTier(null);
      setFormData({
        guest_name: '',
        guest_email: '',
        guest_phone: '',
        guest_city: '',
        guest_country: 'Haïti',
        quantity: 1,
        amount_paid: 50,
        payment_method: 'cash',
        notes: '',
        is_test: false
      });
      
    } catch (error) {
      console.error('Error creating tickets:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création des billets');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Success screen after creating tickets
  if (lastCreated) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-sm p-8 max-w-2xl mx-auto"
      >
        <div className="text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-green-700 mb-2">Billets Créés !</h2>
          <p className="text-gray-600 mb-6">
            {lastCreated.tickets.length} billet(s) pour <strong>{lastCreated.recipient.name}</strong>
          </p>
          
          <div className="bg-gray-50 rounded-xl p-6 mb-6">
            <h3 className="font-semibold mb-4">Numéros des billets :</h3>
            <div className="space-y-2">
              {lastCreated.tickets.map((ticket, index) => (
                <div key={ticket.ticket_id} className="flex items-center justify-center gap-2 p-3 bg-white rounded-lg border-2 border-dashed border-green-300">
                  <Ticket className="w-5 h-5 text-green-600" />
                  <span className="font-mono text-xl font-bold text-green-700">{ticket.ticket_number}</span>
                </div>
              ))}
            </div>
          </div>

          {lastCreated.recipient.email && (
            <p className="text-sm text-gray-500 mb-6 flex items-center justify-center gap-2">
              <Send className="w-4 h-4" />
              Email de confirmation envoyé à {lastCreated.recipient.email}
            </p>
          )}
          
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => setLastCreated(null)}>
              <Ticket className="w-4 h-4 mr-2" />
              Créer d'autres billets
            </Button>
            <Button onClick={() => { setLastCreated(null); setMode('history'); }}>
              Voir l'historique
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header with tabs */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Ticket className="w-5 h-5 text-primary" />
            Émission Manuelle de Billets
          </h2>
          <p className="text-gray-500 text-sm">Pour les paiements Cash, Virement, ou autres moyens hors ligne</p>
        </div>
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setMode('new')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              mode === 'new' ? 'bg-white shadow text-primary' : 'text-gray-600'
            }`}
          >
            Nouveau
          </button>
          <button
            onClick={() => setMode('history')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              mode === 'history' ? 'bg-white shadow text-primary' : 'text-gray-600'
            }`}
          >
            Historique
          </button>
        </div>
      </div>

      {mode === 'new' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: User Selection */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="font-bold mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Bénéficiaire
            </h3>

            {/* Search existing user */}
            {!selectedUser && !isNewPerson && (
              <>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher par nom ou email..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      searchUsers(e.target.value);
                    }}
                    className="pl-10"
                  />
                  {searching && (
                    <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                  )}
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="border rounded-lg divide-y mb-4 max-h-48 overflow-y-auto">
                    {searchResults.map((user) => (
                      <button
                        key={user.user_id}
                        onClick={() => handleSelectUser(user)}
                        className="w-full p-3 text-left hover:bg-gray-50 flex items-center gap-3"
                      >
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <span className="font-bold text-primary">
                            {user.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{user.name}</p>
                          <p className="text-sm text-gray-500 truncate">{user.email}</p>
                        </div>
                        {user.is_guest && (
                          <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded">Guest</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                <div className="text-center py-4 border-t border-b my-4">
                  <p className="text-sm text-gray-500 mb-3">OU</p>
                  <Button variant="outline" onClick={handleNewPerson} className="w-full">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Nouvelle personne (non inscrite)
                  </Button>
                </div>
              </>
            )}

            {/* Selected User Display */}
            {selectedUser && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-green-800">{selectedUser.name}</p>
                    <p className="text-sm text-green-600">{selectedUser.email}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)}>
                    Changer
                  </Button>
                </div>
              </div>
            )}

            {/* New Person Form */}
            {isNewPerson && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <UserPlus className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-blue-800">Nouveau bénéficiaire</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setIsNewPerson(false)} className="text-blue-600">
                    ← Rechercher un utilisateur existant
                  </Button>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Nom complet *</label>
                  <Input
                    value={formData.guest_name}
                    onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                    placeholder="Jean Dupont"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email * <span className="text-red-500 text-xs">(requis pour le tableau de bord)</span></label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      value={formData.guest_email}
                      onChange={(e) => setFormData({ ...formData, guest_email: e.target.value })}
                      placeholder="email@example.com"
                      className="pl-10"
                      required
                    />
                  </div>
                  <p className="text-xs text-green-600 mt-1">✓ Un compte sera créé automatiquement et les billets seront visibles dans le tableau de bord</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Téléphone (optionnel)</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={formData.guest_phone}
                      onChange={(e) => setFormData({ ...formData, guest_phone: e.target.value })}
                      placeholder="+509 ..."
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Ville</label>
                    <Input
                      value={formData.guest_city}
                      onChange={(e) => setFormData({ ...formData, guest_city: e.target.value })}
                      placeholder="Port-au-Prince"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Pays</label>
                    <select
                      value={formData.guest_country}
                      onChange={(e) => setFormData({ ...formData, guest_country: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    >
                      <option value="Haïti">🇭🇹 Haïti</option>
                      <option value="Canada">🇨🇦 Canada</option>
                      <option value="États-Unis">🇺🇸 États-Unis</option>
                      <option value="France">🇫🇷 France</option>
                      <option value="République Dominicaine">🇩🇴 République Dominicaine</option>
                      <option value="Brésil">🇧🇷 Brésil</option>
                      <option value="Autre">🌍 Autre</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right: Ticket Details */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="font-bold mb-4 flex items-center gap-2">
              <Ticket className="w-5 h-5 text-primary" />
              Détails des billets
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Price Tiers Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Sélectionnez un forfait</label>
                <div className="grid grid-cols-2 gap-2">
                  {priceTiers.map((tier, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleTierSelect(tier)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        selectedTier === tier
                          ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-bold text-sm">{tier.label}</div>
                      <div className="text-xs text-gray-500">{tier.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Quantity Input */}
              {selectedTier?.quantity === 'custom' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="block text-sm font-medium mb-2">Quantité personnalisée</label>
                  <div className="flex items-center gap-4">
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={customQuantity}
                      onChange={(e) => handleCustomQuantityChange(e.target.value)}
                      className="w-24"
                    />
                    <div>
                      <span className="text-lg font-bold text-green-600">${formData.amount_paid}</span>
                      <p className="text-xs text-gray-500">
                        {customQuantity >= 3 
                          ? `${Math.floor(customQuantity/3)}×3 billets à $100 + ${customQuantity%3}×$50`
                          : `${customQuantity}×$50`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Manual override for amount */}
              {selectedTier && selectedTier.quantity !== 'custom' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Quantité</label>
                    <div className="px-3 py-2 bg-gray-100 rounded-lg font-bold text-center">
                      {formData.quantity}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Montant ($)</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.amount_paid}
                        onChange={(e) => setFormData({ ...formData, amount_paid: parseFloat(e.target.value) || 0 })}
                        className="pl-10"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Modifiable si nécessaire</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Mode de paiement</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {paymentMethods.map((method) => (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, payment_method: method.id })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.payment_method === method.id
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <method.icon className={`w-5 h-5 mx-auto mb-1 ${method.color}`} />
                      <span className="text-xs font-medium">{method.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Notes (optionnel)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Informations supplémentaires..."
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  rows={2}
                />
              </div>

              {/* Test Ticket Checkbox */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_test}
                    onChange={(e) => setFormData({ ...formData, is_test: e.target.checked })}
                    className="w-5 h-5 rounded border-yellow-400 text-yellow-600 focus:ring-yellow-500"
                  />
                  <div>
                    <span className="font-medium text-yellow-800">🧪 Billet de TEST</span>
                    <p className="text-xs text-yellow-600">Ce billet ne sera PAS comptabilisé dans les statistiques de vente réelles</p>
                  </div>
                </label>
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mt-4">
                <h4 className="font-medium mb-2">Résumé</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Billets</span>
                    <span className="font-bold">{formData.quantity}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Montant</span>
                    <span className="font-bold text-green-600">${formData.amount_paid.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Prix par billet</span>
                    <span>${(formData.amount_paid / formData.quantity).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting || (!selectedUser && !isNewPerson)}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
              >
                {submitting ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Ticket className="w-4 h-4 mr-2" />
                )}
                Générer {formData.quantity} billet(s)
              </Button>
            </form>
          </div>
        </div>
      ) : (
        /* History Tab */
        <div className="bg-white rounded-2xl shadow-sm">
          <div className="p-4 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <h3 className="font-bold">Historique des émissions manuelles ({historyTotal})</h3>
            <div className="flex items-center gap-3">
              {/* Filter Toggle for Test Tickets */}
              <label className="flex items-center gap-2 cursor-pointer bg-gray-100 px-3 py-1.5 rounded-lg">
                <input
                  type="checkbox"
                  checked={showTestTickets}
                  onChange={(e) => {
                    setShowTestTickets(e.target.checked);
                    setHistoryPage(1);
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                />
                <span className="text-sm font-medium text-gray-700">🧪 Afficher les tests</span>
              </label>
              <Button variant="outline" size="sm" onClick={loadManualTickets}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {manualTickets.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Ticket className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Aucune émission manuelle</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Numéro</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bénéficiaire</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paiement</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {manualTickets.map((ticket) => (
                      <tr key={ticket.ticket_id} className={`hover:bg-gray-50 ${ticket.is_test ? 'bg-yellow-50' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-primary">{ticket.ticket_number}</span>
                            {ticket.is_test && (
                              <span className="text-xs bg-yellow-200 text-yellow-800 px-1.5 py-0.5 rounded font-medium">🧪 TEST</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium">{ticket.user_name || '-'}</p>
                            {ticket.is_guest && (
                              <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded">Guest</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium text-green-600">
                          ${ticket.price?.toFixed(2) || '0.00'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            ticket.payment_method === 'cash' ? 'bg-green-100 text-green-700' :
                            ticket.payment_method === 'transfer' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {ticket.payment_method?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {formatDate(ticket.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <a
                              href={`${API}/tickets/view/${ticket.ticket_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 hover:bg-blue-100 rounded-lg transition-colors text-blue-600"
                              title="Voir le billet"
                            >
                              <Eye className="w-4 h-4" />
                            </a>
                            <a
                              href={`${API}/tickets/download/${ticket.ticket_id}`}
                              className="p-2 hover:bg-green-100 rounded-lg transition-colors text-green-600"
                              title="Télécharger PDF"
                            >
                              <Download className="w-4 h-4" />
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {historyPages > 1 && (
                <div className="p-4 border-t flex justify-between items-center">
                  <span className="text-sm text-gray-500">Page {historyPage} / {historyPages}</span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                      disabled={historyPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setHistoryPage(p => Math.min(historyPages, p + 1))}
                      disabled={historyPage >= historyPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default ManualTicketManager;
